import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { IntlShape, useIntl } from 'react-intl';
import { Field, FormikErrors, useFormikContext } from 'formik';
import { Tooltip } from '@sdir/sds';
import { OccupationReferenceExtended } from '@sdir/httpclient/lib/clients/aps/seagoing';
import moment, { Moment } from 'moment';
import { INumberService } from '@sdir/utilities/lib/services/NumberService';
import { SelectOption } from '../../../../../types';
import { FormDate, FormInput, FormLinkSwitch, FormSelect } from '../../../../Molecules';
import { sortListByAttribute } from '../../../../../helpers/sortHelpers';

export interface SeagoingDetailsInput {
  occupationCode: string;
  startDate: Moment | null;
  endDate: Moment | null;
  useHoursPerDay: boolean;
  totalHours: string;
  hoursPerDay: string;
}

export const validateSeagoingDetails = (
  values: SeagoingDetailsInput,
  intl: IntlShape,
  numberService: INumberService
) => {
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const overlappingErrorText = intl.formatMessage({
    id: 'general.form.error.startdatemustbebeforeenddate',
  });
  const invalidNumberText = intl.formatMessage({ id: 'general.form.error.invalidnumberformat' });

  const errors: FormikErrors<SeagoingDetailsInput> = {};

  if (!values.occupationCode) errors.occupationCode = requiredText;
  if (!values.startDate) errors.startDate = requiredText;
  if (!values.endDate) errors.endDate = requiredText;
  if (values.startDate && values.endDate && values.startDate > values.endDate) {
    errors.startDate = overlappingErrorText;
    errors.endDate = overlappingErrorText;
  }
  if (values.useHoursPerDay) {
    if (!values.hoursPerDay) errors.hoursPerDay = requiredText;
    else if (!numberService.isValid(values.hoursPerDay)) errors.hoursPerDay = invalidNumberText;
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!values.totalHours) errors.totalHours = requiredText;
    else if (!numberService.isValid(values.totalHours)) errors.totalHours = invalidNumberText;
  }

  return Object.keys(errors).length ? errors : undefined;
};

export const createSeagoingDetailsInitialValues = (): SeagoingDetailsInput => ({
  occupationCode: '',
  startDate: null,
  endDate: null,
  useHoursPerDay: true,
  totalHours: '',
  hoursPerDay: '',
});

export interface SeagoingDetailsSubFormProps {
  name: string;
  occupations: OccupationReferenceExtended[];
}

const DateDisplayFormat = 'DD.MM.YYYY';

export const SeagoingDetailsSubForm: FC<SeagoingDetailsSubFormProps> = ({ name, occupations }) => {
  const intl = useIntl();

  const occupationOptions = useMemo(
    () =>
      sortListByAttribute(
        occupations.map<SelectOption>((o) => ({
          name: o.title,
          value: o.code,
          group: o.category,
        })),
        'name'
      ),
    [occupations]
  );

  const makeFullFieldName = useCallback((fieldName) => `${name}.${fieldName}`, [name]);

  const { getFieldProps } = useFormikContext();

  const fieldProps = getFieldProps<SeagoingDetailsInput>(name);

  return (
    <FieldsContainer>
      <Field
        name={makeFullFieldName('occupationCode')}
        component={StyledFormSelect}
        grouped
        searchable
        options={occupationOptions}
        defaultVal={intl.formatMessage({
          id: 'addseagoing.details.jobtitle',
        })}
      />
      <Field
        format={DateDisplayFormat}
        maxDate={moment()}
        name={makeFullFieldName('startDate')}
        placeholder={intl.formatMessage({
          id: 'addseagoing.details.periodstart',
        })}
        type="number"
        showYearDropdown
        showMonthDropdown
        component={FormDate}
      />
      <Field
        format={DateDisplayFormat}
        maxDate={moment()}
        name={makeFullFieldName('endDate')}
        placeholder={intl.formatMessage({
          id: 'addseagoing.details.periodend',
        })}
        type="number"
        showYearDropdown
        showMonthDropdown
        component={FormDate}
      />
      <div className="hours-input">
        {fieldProps?.value?.useHoursPerDay ? (
          <Field
            name={makeFullFieldName('hoursPerDay')}
            type="text"
            className="hours-field"
            component={FormInput}
            label={intl.formatMessage({
              id: 'addseagoing.details.hoursperday',
            })}
          />
        ) : (
          <Field
            name={makeFullFieldName('totalHours')}
            type="text"
            className="hours-field"
            component={FormInput}
            label={intl.formatMessage({
              id: 'addseagoing.details.totalhours',
            })}
          />
        )}
        <Tooltip className="hours-per-day-tooltip" placement="top-left">
          {intl.formatMessage({ id: 'addseagoing.details.hourstooltip' })}
        </Tooltip>
        <Field
          className="hours-per-day-toggle"
          name={makeFullFieldName('useHoursPerDay')}
          activeLabel={intl.formatMessage({
            id: 'addseagoing.details.settotalhours',
          })}
          inactiveLabel={intl.formatMessage({
            id: 'addseagoing.details.sethoursperday',
          })}
          component={FormLinkSwitch}
        />
      </div>
    </FieldsContainer>
  );
};

const StyledFormSelect = styled(FormSelect)`
  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    & > div > div > div[open] {
      width: 300px;
    }
  }
`;

const FieldsContainer = styled.div`
  margin-bottom: 5rem;
  display: grid;
  grid-template-columns: 1fr 8rem;

  > * {
    margin: 0 0 3rem;
    grid-column: 1;

    &:last-child {
      margin-bottom: 0.5rem;
    }
  }

  .hours-input {
    grid-column: 1 / span 2;
    display: grid;
    grid-column-gap: 2rem;
    grid-template-columns: 1fr 6rem;
  }

  .hours-field .tf-row > input:valid + label > span {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }

  .hours-per-day-tooltip {
    grid-column: 2;
    grid-row: 1;
  }

  .hours-per-day-toggle {
    grid-column: 1;
    grid-row: 2;
    margin-top: 0;
    display: flex;
    justify-content: flex-end;
  }
`;
